import { FunctionComponent, ReactNode, CSSProperties, useState } from 'react';
import { connect } from 'react-redux';
import { WebEntity } from '../entities/types';
import { SimpleObject, StudyConfigType, StudyType } from '../types';
import { addStudy, refreshMapElements } from '../actions/middleActions';
import { toast } from 'react-toastify';
import Modal from '@mui/material/Modal';
import RefreshIcon from '@mui/icons-material/Refresh';
import Swal from 'sweetalert2';
import { Button, ButtonPropsVariantOverrides, ButtonPropsColorOverrides } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';

type ButtonParams = {
  action: (params?: any, webEntity?: WebEntity<SimpleObject>) => Promise<any>;
};

type Props = {
  action: (params?: any) => Promise<any>;
  onClick: (buttonParams: ButtonParams) => Promise<void>;
  children: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
};

const SecondaryButton: FunctionComponent<Props> = ({
  action,
  onClick,
  children,
  disabled = false,
  style = {}
}) => {
  const handleOnClick = async () => {
    await onClick({ action });
  };

  return (
    <button
      className='primary-button-style'
      onClick={handleOnClick}
      disabled={disabled}
      style={style}>
      {children}
    </button>
  );
};

export const RefreshMapElements: FunctionComponent = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const ConnectedBtn = connectedSecondaryButton(refreshMapElements);

  const onClick = async (buttonParams: ButtonParams) => {
    const { action } = buttonParams;

    toast.promise(
      async () => {
        try {
          setLoading(true);
          const res = await action();
          setLoading(false);
          if (res && res.status === 200) {
            return res;
          }
          throw Error;
        } catch (error) {
          throw error;
        }
      },
      {
        pending: 'Cargando....',
        success: `Los elementos del mapa no se refrescaron correctamente:`,
        error: 'Error, no se puede refrescar los elementos del mapa'
      }
    );
  };

  return (
    <ConnectedBtn onClick={onClick} disabled={loading}>
      <RefreshIcon />
      Refrescar Mapa
    </ConnectedBtn>
  );
};

const connectedSecondaryButton = (action: any) => connect(null, { action })(SecondaryButton);

export const AddStudyButton: FunctionComponent<{
  nombreSimulacion: string;
  descripcionSimulacion: string;
  typeStudy: StudyType;
  alimentadorId: string;
  config: StudyConfigType;
  clearForm: () => void;
  goToMyStudies: () => void;
}> = ({
  nombreSimulacion,
  descripcionSimulacion,
  typeStudy,
  alimentadorId,
  config,
  clearForm,
  goToMyStudies
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const ConnectedBtn = connectedSecondaryButton(addStudy);

  const configCheckbox = [
    config?.pvResidencial?.checked,
    config?.batResidencial?.checked,
    config?.tapDinamico,
    config?.reactivosPv,
    config?.bateriasResidenciales,
    config?.evResidencialCheck
  ];

  const onClick = async (buttonParams: ButtonParams) => {
    const { action } = buttonParams;

    if (!nombreSimulacion) {
      Swal.fire({
        icon: 'warning',
        title: `¡Falta el Nombre del Estudio!`,
        showCloseButton: true
      });
      return;
    } else if (!descripcionSimulacion) {
      Swal.fire({
        icon: 'warning',
        title: `¡Falta la Descripción del Estudio!`,
        showCloseButton: true
      });
      return;
    } else if (!alimentadorId) {
      Swal.fire({
        icon: 'warning',
        title: `¡No has seleccionado un alimentador!`,
        showCloseButton: true
      });
      return;
    } else if (configCheckbox.every((c) => !c)) {
      Swal.fire({
        icon: 'warning',
        title: `¡No has seleccionado una configuración!`,
        showCloseButton: true
      });
      return;
    }

    toast.promise(
      async () => {
        setLoading(true);
        const res = await action({
          nombreSimulacion,
          descripcionSimulacion,
          typeStudy,
          alimentadorId,
          config
        });
        setLoading(false);
        if (res && res?.status === 200) {
          clearForm();
          handleOpen();
          return res;
        }
        throw Error;
      },
      {
        pending: 'Cargando....',
        success: `El estudio se ejecutó correctamente`,
        error: 'Error, no se pudo ejecutar el Estudio'
      }
    );
  };

  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div
          style={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'clamp(340px, 30vw, 480px)',
            backgroundColor: 'var(--vdr-white)',
            padding: '24px 28px 28px',
            borderRadius: 0,
            borderTop: '4px solid var(--vdr-cyan)',
            boxShadow: 'var(--vdr-shadow-overlay)',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '12px',
              borderBottom: '1px solid var(--vdr-line)'
            }}>
            <h2
              style={{
                fontFamily: 'var(--vdr-font-display)',
                fontSize: '22px',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: 'var(--vdr-ink)',
                margin: 0
              }}>
              ¡GRACIAS!
            </h2>
            <p
              onClick={() => handleClose()}
              style={{
                fontFamily: 'var(--vdr-font-mono)',
                fontSize: '16px',
                fontWeight: 500,
                color: 'var(--vdr-mute)',
                cursor: 'pointer',
                margin: 0
              }}>
              X
            </p>
          </div>
          <p
            style={{
              fontFamily: 'var(--vdr-font-body)',
              fontSize: '16px',
              lineHeight: 1.5,
              color: 'var(--vdr-text-2)',
              margin: 0
            }}>
            Tu estudio ha sido recibido exitosamente, puedes revisar el estado en Mis Estudios.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className='primary-button-style' onClick={() => goToMyStudies()}>
              Revisar Mis Estudios
            </button>
          </div>
        </div>
      </Modal>
      <ConnectedBtn
        onClick={onClick}
        disabled={
          loading ||
          !nombreSimulacion ||
          !descripcionSimulacion ||
          !alimentadorId ||
          configCheckbox.every((c) => !c)
        }>
        Ejecutar Estudio
      </ConnectedBtn>
    </>
  );
};
